import { useState, useMemo } from "react";
import { IonNote } from "@ionic/react";
import { CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";

// The card form shared by the subscription checkout and the Payment methods
// modal: Stripe Elements for the card (the details never touch our inputs),
// plus country and ZIP code. `fields` is the form to render; the rest reads
// what was entered.

export const COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"], ["AU", "Australia"], ["MX", "Mexico"],
  ["DE", "Germany"], ["FR", "France"], ["ES", "Spain"], ["IT", "Italy"], ["NL", "Netherlands"], ["IE", "Ireland"],
  ["NZ", "New Zealand"], ["BR", "Brazil"], ["IN", "India"], ["PH", "Philippines"], ["ZA", "South Africa"],
];

// How a saved card reads: "Visa •••• 4242", plus the wallet it came from
const BRANDS = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay" };
const WALLETS = { apple_pay: "Apple Pay", google_pay: "Google Pay", link: "Link", samsung_pay: "Samsung Pay" };
export const brandName = (b) => BRANDS[String(b || "").toLowerCase()] || (b ? String(b).charAt(0).toUpperCase() + String(b).slice(1) : "Card");
export const cardTitle = (c) => `${brandName(c.brand)} •••• ${c.last4}${c.wallet && WALLETS[c.wallet] ? ` · ${WALLETS[c.wallet]}` : ""}`;
export const cardExpiry = (c) => (c.expMonth && c.expYear ? `Expires ${String(c.expMonth).padStart(2, "0")}/${String(c.expYear).slice(-2)}` : "");
// The list rows: a one-line name ("Visa •••• 4242") and a note line ("Apple Pay · Expires 11/28 · Default")
export const cardName = (c) => `${brandName(c.brand)} •••• ${c.last4}`;
export const cardNote = (c, isDefault) => [c.wallet && WALLETS[c.wallet], cardExpiry(c), isDefault ? "Default" : ""].filter(Boolean).join(" · ");

// Stripe's mark: the purple tile with the S
export function StripeMark() {
  return (
    <span className="stripe-logo" aria-label="Stripe">
      <svg viewBox="0 0 24 24" role="img" aria-hidden="true"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z" /></svg>
    </span>
  );
}

export function useCardForm({ disabled = false } = {}) {
  const [focused, setFocused] = useState(null);
  const [cardErrors, setCardErrors] = useState({});
  const [country, setCountry] = useState("US");
  const [zip, setZip] = useState("");

  // Stripe iframes cannot read our CSS variables, so resolve the theme once
  const stripeStyle = useMemo(() => {
    const dark = document.body.classList.contains("dark");
    return {
      base: {
        color: dark ? "#f4f5f8" : "#111827",
        fontSize: "16px",
        fontFamily: '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif',
        "::placeholder": { color: dark ? "rgba(244,245,248,0.45)" : "#9ca3af" },
        iconColor: dark ? "#9ba0a6" : "#6b7280",
      },
      invalid: { color: "#f04141", iconColor: "#f04141" },
    };
  }, []);
  const box = (key) => `checkout-box${focused === key ? " is-focus" : ""}${cardErrors[key] ? " is-error" : ""}`;
  const elementProps = (key) => ({
    onFocus: () => setFocused(key),
    onBlur: () => setFocused((f) => (f === key ? null : f)),
    onChange: (ev) => setCardErrors((prev) => ({ ...prev, [key]: ev.error ? ev.error.message : null })),
    options: { style: stripeStyle, disabled, ...(key === "number" ? { showIcon: true } : {}) },
  });

  const firstError = () => ["number", "expiry", "cvc"].map((k) => cardErrors[k]).find(Boolean) || (!zip.trim() ? "Please enter your ZIP code." : "");
  const billingAddress = () => ({ country, postal_code: zip.trim() });
  const reset = () => { setZip(""); setCardErrors({}); setFocused(null); };

  const fields = (
    <div className="stripe-card-form">
      <div>
        <label>Card number</label>
        <div className={box("number")}><CardNumberElement {...elementProps("number")} /></div>
        {cardErrors.number && <IonNote color="danger" className="checkout-field-error">{cardErrors.number}</IonNote>}
      </div>
      <div className="checkout-two">
        <div>
          <label>Expiration date</label>
          <div className={box("expiry")}><CardExpiryElement {...elementProps("expiry")} /></div>
          {cardErrors.expiry && <IonNote color="danger" className="checkout-field-error">{cardErrors.expiry}</IonNote>}
        </div>
        <div>
          <label>Security code</label>
          <div className={box("cvc")}><CardCvcElement {...elementProps("cvc")} /></div>
          {cardErrors.cvc && <IonNote color="danger" className="checkout-field-error">{cardErrors.cvc}</IonNote>}
        </div>
      </div>
      <div>
        <label>Country</label>
        <div className={box("country")}>
          <select value={country} disabled={disabled} onChange={(e) => setCountry(e.target.value)} onFocus={() => setFocused("country")} onBlur={() => setFocused((f) => (f === "country" ? null : f))} aria-label="Country">
            {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label>ZIP code</label>
        <div className={box("zip")}>
          <input type="text" inputMode="numeric" autoComplete="postal-code" placeholder="12345" value={zip} disabled={disabled} onChange={(e) => setZip(e.target.value)} onFocus={() => setFocused("zip")} onBlur={() => setFocused((f) => (f === "zip" ? null : f))} aria-label="ZIP code" />
        </div>
      </div>
    </div>
  );

  return { fields, firstError, billingAddress, reset, country, zip };
}

// The brand mark shown beside a saved card (Visa, Mastercard, American
// Express, Discover), or the wallet it came from (Apple Pay, Google Pay); a
// plain card for anything else. Small inline SVGs, no network.
export function CardBrandIcon({ brand, wallet, slot }) {
  const b = String(wallet || brand || "").toLowerCase();
  let art;
  if (b === "apple_pay") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="apple_pay"><rect width="36" height="24" rx="4" fill="#000" /><path fill="#fff" d="M11.9 8.4c.5-.6.8-1.4.7-2.2-.7 0-1.5.5-2 1.1-.4.5-.8 1.3-.7 2.1.8.1 1.5-.4 2-1zm.7 1.2c-1.1-.1-2 .6-2.6.6-.5 0-1.3-.6-2.2-.6-1.1 0-2.2.7-2.8 1.7-1.2 2.1-.3 5.1.9 6.8.6.8 1.2 1.8 2.1 1.7.8 0 1.2-.5 2.2-.5s1.3.5 2.2.5c.9 0 1.5-.8 2.1-1.7.7-.9.9-1.9.9-1.9s-1.8-.7-1.8-2.7c0-1.7 1.4-2.5 1.5-2.6-.8-1.2-2-1.3-2.5-1.3z" /><text x="17" y="16.5" fill="#fff" fontSize="9" fontWeight="700" fontFamily="Helvetica, Arial, sans-serif">Pay</text></svg>
    );
  } else if (b === "google_pay") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="google_pay"><rect width="36" height="24" rx="4" fill="#fff" stroke="#e0e0e0" /><text x="5" y="16.5" fontSize="11" fontWeight="700" fontFamily="Helvetica, Arial, sans-serif"><tspan fill="#4285F4">G</tspan><tspan fill="#5f6368" dx="1.5">Pay</tspan></text></svg>
    );
  } else if (b === "visa") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="visa"><rect width="36" height="24" rx="4" fill="#1A1F71" /><text x="18" y="16.5" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="800" fontStyle="italic" fontFamily="Helvetica, Arial, sans-serif">VISA</text></svg>
    );
  } else if (b === "mastercard") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="mastercard"><rect width="36" height="24" rx="4" fill="#252525" /><circle cx="14.5" cy="12" r="6.5" fill="#EB001B" /><circle cx="21.5" cy="12" r="6.5" fill="#F79E1B" /><path fill="#FF5F00" d="M18 6.9a6.5 6.5 0 0 0 0 10.2 6.5 6.5 0 0 0 0-10.2z" /></svg>
    );
  } else if (b === "amex") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="amex"><rect width="36" height="24" rx="4" fill="#2E77BC" /><text x="18" y="16" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="800" fontFamily="Helvetica, Arial, sans-serif">AMEX</text></svg>
    );
  } else if (b === "discover") {
    art = (
      <svg viewBox="0 0 36 24" data-brand="discover"><rect width="36" height="24" rx="4" fill="#fff" stroke="#e0e0e0" /><circle cx="27" cy="12" r="4.5" fill="#F58220" /><text x="4" y="15.5" fill="#231F20" fontSize="7" fontWeight="800" fontFamily="Helvetica, Arial, sans-serif">DISCOVER</text></svg>
    );
  } else {
    art = (
      <svg viewBox="0 0 36 24" data-brand="card"><rect width="36" height="24" rx="4" fill="#9ba0a6" /><rect x="0" y="6" width="36" height="4" fill="#5f6368" /><rect x="5" y="14" width="12" height="3" rx="1" fill="#fff" /></svg>
    );
  }
  return <span slot={slot} className="card-brand-icon" aria-hidden="true">{art}</span>;
}
